import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import type { ImportantNotice } from '../../features/notices';
import ClassDescriptionEditor from './ClassDescriptionEditor';

interface NoticeModalPropsBase {
  show: boolean;
  onHide: () => void;
  saving: boolean;
}

interface NoticeModalPropsAdd extends NoticeModalPropsBase {
  mode: 'add';
  onSubmitAdd: (formData: FormData) => Promise<void>;
  onSubmitEdit?: never;
  notice?: never;
}

interface NoticeModalPropsEdit extends NoticeModalPropsBase {
  mode: 'edit';
  notice: ImportantNotice;
  onSubmitEdit: (id: string, data: { title: string; descriptionHtml: string | null }) => Promise<void>;
  onSubmitAdd?: never;
}

type NoticeModalProps = NoticeModalPropsAdd | NoticeModalPropsEdit;

function plainTextToEditorHtml(text: string | null | undefined): string {
  const value = (text ?? '').trim();
  if (!value) return '';

  const escaped = value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  return escaped
    .split(/\r?\n/)
    .map((line) => `<p>${line || '<br>'}</p>`)
    .join('');
}

const NoticeModal: React.FC<NoticeModalProps> = (props) => {
  const { show, mode, onHide, saving } = props;
  const notice = 'notice' in props ? props.notice : null;
  // Use form data pattern like EventModal/ProjectModal
  const [formData, setFormData] = useState({
    title: '',
    descriptionHtml: '',
  });
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (notice) {
      const fallbackDescriptionHtml = plainTextToEditorHtml(notice.description);
      // Editing mode - populate from notice
      setFormData({
        title: notice.title ?? 'Important Notice',
        descriptionHtml: notice.descriptionHtml ?? fallbackDescriptionHtml,
      });
    } else {
      // Add mode - reset to empty
      setFormData({
        title: '',
        descriptionHtml: '',
      });
    }
    setError(null);
  }, [notice, show]);

  const handleSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (props.mode !== 'add' || !props.onSubmitAdd) return;
    setError(null);

    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setError('Please select an image (PNG or JPEG).');
      return;
    }

    const stateHtml = DOMPurify.sanitize(formData.descriptionHtml).trim();
    const htmlContent = stateHtml;
    const submitFormData = new FormData();
    submitFormData.append('file', file);
    submitFormData.append('title', formData.title.trim() || 'Important Notice');
    if (htmlContent.trim()) submitFormData.append('descriptionHtml', htmlContent.trim());

    try {
      await props.onSubmitAdd(submitFormData);
      onHide();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create notice');
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (props.mode !== 'edit' || !props.notice || !props.onSubmitEdit) return;
    setError(null);
    const stateHtml = DOMPurify.sanitize(formData.descriptionHtml).trim();
    const htmlContent = stateHtml;
    try {
      await props.onSubmitEdit(props.notice.id, {
        title: formData.title.trim() || 'Important Notice',
        descriptionHtml: htmlContent.trim() || null,
      });
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update notice');
    }
  };

  const handleSubmit = mode === 'add' ? handleSubmitAdd : handleSubmitEdit;

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-megaphone me-2" />
              {mode === 'add' ? 'Add Notice' : 'Edit Notice'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onHide} disabled={saving} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger mb-3" role="alert">
                  <i className="bi bi-exclamation-triangle me-2" />
                  {error}
                </div>
              )}

              {/* Title Field */}
              <div className="mb-3">
                <label className="form-label">
                  <i className="bi bi-type me-2"></i>Title *
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter notice title"
                  required
                />
                <small className="text-muted">This title will be shown in the fullscreen view</small>
              </div>

              {mode === 'add' && (
                <div className="mb-3">
                  <label className="form-label">
                    <i className="bi bi-image me-2"></i>Image (required)
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="form-control"
                    accept="image/png,image/jpeg,image/jpg"
                    required
                  />
                  <small className="text-muted">PNG or JPEG, max 10MB</small>
                </div>
              )}

              {/* Rich Text Description */}
              <div className="mb-3">
                <label className="form-label">
                  <i className="bi bi-file-text me-2"></i>Description
                </label>
                <ClassDescriptionEditor
                  key={notice?.id ?? 'new'}
                  html={formData.descriptionHtml}
                  onHtmlChange={(html) => setFormData((prev) => ({ ...prev, descriptionHtml: html }))}
                />
                <small className="text-muted">
                  This description will be shown in the fullscreen view
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onHide} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Saving…
                  </>
                ) : mode === 'add' ? (
                  'Add Notice'
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NoticeModal;
